import React from 'react';
import { useNavigate } from 'react-router-dom';

const ComplianceSection: React.FC = () => {
  const navigate = useNavigate();
  const complianceFeatures = [
    {
      icon: '🏛️',
      title: 'AB 1482 Compliance',
      description: 'Prompts for rent cap and just-cause language commonly used in California.',
      status: 'Guided'
    },
    {
      icon: '📋',
      title: 'Required Disclosures', 
      description: 'Surfaces state-required disclosures so you can confirm what applies.',
      status: 'Guided'
    },
    {
      icon: '🔔',
      title: 'Legal Notices',
      description: 'Highlights common notice timelines and tenant-rights summaries.',
      status: 'Guided'
    },
    {
      icon: '🏅',
      title: 'Habitability Standards',
      description: 'Language aligned with typical California habitability obligations.',
      status: 'Guided'
    },
    {
      icon: '💰',
      title: 'Security Deposit Laws',
      description: 'Reminders about deposit limits and return timelines to review.',
      status: 'Guided'
    },
    {
      icon: '⚖️',
      title: 'Fair Housing Protection',
      description: 'Anti-discrimination language to support fair housing obligations.',
      status: 'Guided'
    }
  ];

  const legalUpdates = [
    { year: '2024', update: 'Updated for latest California rental laws' },
    { year: '2023', update: 'AB 1482 rent control provisions updated' },
    { year: '2023', update: 'New disclosure requirements added' },
    { year: '2022', update: 'COVID-19 rental protections integrated' }
  ];

  return (
    <div style={{
      background: '#000000',
      padding: '120px 0',
      position: 'relative',
      color: 'white'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 40% 40%, rgba(16, 185, 129, 0.2) 0%, transparent 50%)`,
        opacity: 0.4
      }}></div>

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Section Header */}
        <div className="compliance-header" style={{
          textAlign: 'center',
          marginBottom: '80px'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: 'rgba(34, 197, 94, 0.15)',
            border: '1px solid rgba(34, 197, 94, 0.25)',
            borderRadius: '100px',
            marginBottom: '30px'
          }}>
            <span style={{ fontSize: '16px' }}>🛡️</span>
            <span style={{
              color: '#22C55E',
              fontSize: '0.875rem',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
              California Focused
            </span>
          </div>

          <h2 className="compliance-title" style={{
            fontSize: 'clamp(2.75rem, 5vw, 3.75rem)',
            fontWeight: 700,
            marginBottom: '24px',
            letterSpacing: '-0.02em'
          }}>
            Built for California
            <span style={{ color: '#3B82F6' }}> lease requirements</span>
          </h2>
          <p className="compliance-subtitle" style={{
            fontSize: 'clamp(1.2rem, 2.6vw, 1.4rem)',
            color: '#CBD5E1',
            maxWidth: '640px',
            margin: '0 auto',
            lineHeight: 1.7
          }}>
            Guided checklists and template language designed around common California laws.
          </p>
        </div>

        {/* Compliance Features Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '32px',
          marginBottom: '80px'
        }}>
          {complianceFeatures.map((feature, index) => (
            <div key={index} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '32px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}>
                <div style={{
                  fontSize: '32px',
                  lineHeight: 1
                }}>
                  {feature.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                  }}>
                    <h3 style={{
                      fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
                      fontWeight: 600,
                      margin: 0,
                      color: 'white'
                    }}>
                      {feature.title}
                    </h3>
                    <span style={{
                      background: 'rgba(34, 197, 94, 0.2)',
                      color: '#22C55E',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {feature.status}
                    </span>
                  </div>
                  <p style={{
                    color: '#CBD5E1',
                    margin: 0,
                    lineHeight: 1.5,
                    fontSize: '0.95rem'
                  }}>
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* <p style={{ color: '#CBD5E1', maxWidth: '820px', margin: '0 auto', textAlign: 'center', lineHeight: 1.6 }}>
          Use these tools as a starting point and check any city-specific requirements before you finalize.
        </p> */}

        {/* Legal updates timeline intentionally hidden */}

        {/* Compliance CTA intentionally hidden */}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .compliance-header {
            margin-bottom: 60px !important;
            padding: 0 20px !important;
          }
          
          .compliance-title {
            font-size: 2.25rem !important;
            margin-bottom: 20px !important;
          }
          
          .compliance-subtitle {
            font-size: 1.125rem !important;
            padding: 0 10px !important;
          }
          
          .compliance-features-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          
          .compliance-feature-card {
            padding: 24px !important;
          }
          
          .compliance-updates {
            padding: 32px 24px !important;
          }
          
          .compliance-updates-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
        }

        @media (max-width: 480px) {
          .compliance-header {
            margin-bottom: 40px !important;
            padding: 0 16px !important;
          }
          
          .compliance-title {
            font-size: 1.75rem !important;
            margin-bottom: 16px !important;
          }
          
          .compliance-subtitle {
            font-size: 1rem !important;
            padding: 0 !important;
          }
          
          .compliance-feature-card {
            padding: 20px !important;
          }
          
          .compliance-updates {
            padding: 24px 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ComplianceSection;
